// Text-input date field that renders as DD-MM-YYYY and round-trips ISO
// strings (YYYY-MM-DD) at its props boundary. Sidesteps the Chromium
// behaviour where <input type="date"> uses the browser's UI language
// for its placeholder + segment display ("mm/dd/yyyy") regardless of
// the document's lang attribute.
//
// Caller passes the parent state as ISO; we manage the display string
// internally so partial typing ("15-05-2") doesn't get clobbered when
// the parent stays at the previous good ISO until parsing succeeds.

import { Show, createEffect, createSignal, type Accessor } from "solid-js";
import { TextTooltip } from "./TextTooltip";
import { displayToIso, isoToDisplay, sanitiseDate } from "./helpers";
import s from "./DateField.module.css";

export interface DateFieldProps {
  value: Accessor<string>;          // ISO YYYY-MM-DD or ""
  setValue: (iso: string) => void;
  id?: string;
  ariaLabel?: string;
}

export function DateField(props: DateFieldProps) {
  // Track what the parent last has so external resets (props.value
  // changes from outside our onInput) reflect into the display, while
  // intermediate user typing isn't clobbered.
  let lastParent: string = props.value();
  const [text, setText] = createSignal(isoToDisplay(props.value()));

  createEffect(() => {
    const ext = props.value();
    if (ext !== lastParent) {
      setText(isoToDisplay(ext));
      lastParent = ext;
    }
  });

  function onInput(e: InputEvent & { currentTarget: HTMLInputElement }) {
    const next = sanitiseDate(e.currentTarget.value);
    setText(next);
    const iso = displayToIso(next);
    if (iso !== lastParent) {
      lastParent = iso;
      props.setValue(iso);
    }
  }

  function clear() {
    setText("");
    if (lastParent !== "") {
      lastParent = "";
      props.setValue("");
    }
  }

  return (
    <div class={s.wrap}>
      <input
        id={props.id}
        type="text"
        inputmode="numeric"
        maxlength="10"
        pattern="^\d{2}-\d{2}-\d{4}$"
        placeholder="DD-MM-YYYY"
        value={text()}
        onInput={onInput}
        aria-label={props.ariaLabel}
      />
      <Show when={text()}>
        <TextTooltip text="Clear" description="Reset the date field">
          <button
            type="button"
            class={s.clear}
            onClick={clear}
            aria-label="Clear date"
          >×</button>
        </TextTooltip>
      </Show>
    </div>
  );
}
